import React, {
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../components/datatable/DataTable";
import { ContentHeader } from "@components";
import { useAuth } from "../contexts/AuthContext";
import API from "../api";
import toastr from "toastr";

interface UnpaidBill {
  id: number;
  bill_date: string;
  bill_no: string;
  bill_type: "patient_entry" | "billing";
  patient_id: number;
  P_code: string;
  P_name: string;
  prefix: string;
  net_amount: number;
  paid_amount: number;
  balance_amount: number;
}

interface PatientBill {
  id: number;
  bill_date: string;
  bill_no: string;
  bill_type: "patient_entry" | "billing";
  bill_amount: number;
  paid_amount: number;
  balance_amount: number;
}

interface PatientFinanceData {
  patient: {
    id: number;
    P_code: string;
    P_name: string;
    prefix: string;
    mobile: string;
  };
  bills: PatientBill[];
  totals: {
    total_bill_amount: string;
    total_paid_amount: string;
    total_balance_amount: string;
    payable_amount: string;
  };
  pending_count: number;
}

interface PaymentReceipt {
  id: number;
  created: string;
  customer_id: number;
  patient_name: string;
  P_code: string;
  bill_id: number;
  bill_type: string;
  bill_no: string;
  description: string;
  amount: number;
  payment_method: string;
  reference_no: string;
  source_table: "customer_payments" | "payments"; // ✅ added
}

function fmt(v: unknown): string {
  if (!v) return "—";
  const d = new Date(v as string);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString("en-IN");
}
function fmtAmt(v: unknown): string {
  const n = parseFloat(v as string);
  return isNaN(n) ? "0.00" : n.toFixed(2);
}

// ─────────────────────────────────────────────────────────────────────────────
// PatientFinanceModal
// ─────────────────────────────────────────────────────────────────────────────
const PatientFinanceModal: React.FC<{
  patientId: number | null;
  onClose: () => void;
  onSuccess: () => void;
  currentUser: string;
}> = ({ patientId, onClose, onSuccess, currentUser }) => {
  const [data, setData]           = useState<PatientFinanceData | null>(null);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("Cash");
  const [refNo, setRefNo]         = useState("");

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    setPayAmount("");
    setPayMethod("Cash");
    setRefNo("");

    API.get(`/payments/getPatientBills/${patientId}`)
      .then((r) => {
        setData(r.data);
        setPayAmount(r.data.totals.payable_amount);
        if (r.data.pending_count > 0) {
          setTimeout(() => {
            toastr.warning(
              `${r.data.pending_count} Bill(s) Pending for ${r.data.patient.prefix} ${r.data.patient.P_name}`,
              "",
              { timeOut: 4000 }
            );
          }, 400);
        }
      })
      .catch(() => toastr.error("Failed to load patient bills"))
      .finally(() => setLoading(false));
  }, [patientId]);

  const handlePayAmountChange = (val: string) => {
    if (!data) return;
    const payable = parseFloat(data.totals.payable_amount);
    if (parseFloat(val) > payable) {
      toastr.warning("Pay amount cannot exceed the pending balance");
      setPayAmount(fmtAmt(payable));
      return;
    }
    setPayAmount(val);
  };

  const handleSubmit = async () => {
    if (!data || !patientId) return;
    const amount = parseFloat(payAmount);
    if (!amount || amount <= 0) {
      toastr.warning("Enter a valid pay amount");
      return;
    }
    setSaving(true);
    try {
      await API.post("/payments/savePayment", {
        customer_id:    patientId,
        pay_amount:     amount,
        payment_method: payMethod,
        reference_no:   refNo,
        bill_data:      data.bills,
      });
      toastr.success("Payment saved successfully");
      onSuccess();
    } catch {
      toastr.error("Failed to save payment");
    } finally {
      setSaving(false);
    }
  };

  if (!patientId) return null;

  const payAmountNum = parseFloat(payAmount) || 0;
  const payable      = data ? parseFloat(data.totals.payable_amount) : 0;
  const isValid      = payAmountNum > 0 && payAmountNum <= payable;

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fa fa-wallet me-2 text-primary" />
              {data
                ? `${data.patient.prefix} ${data.patient.P_name} — Patient Finance`
                : "Patient Finance"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>

          <div className="modal-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" />
                <p className="mt-2 text-muted">Loading bills…</p>
              </div>
            ) : !data ? (
              <div className="alert alert-warning">No data found.</div>
            ) : (
              <>
                <div className="d-flex gap-3 mb-3 flex-wrap">
                  {[
                    { label: "Patient ID",    value: data.patient.P_code },
                    { label: "Mobile",        value: data.patient.mobile || "—" },
                    { label: "Pending Bills", value: String(data.pending_count) },
                  ].map(({ label, value }) => (
                    <div
                      key={label}
                      className="border rounded px-3 py-2"
                      style={{ minWidth: 140 }}
                    >
                      <div className="text-muted" style={{ fontSize: 11 }}>{label}</div>
                      <div className="fw-semibold">{value}</div>
                    </div>
                  ))}
                </div>

                <div className="table-responsive mb-4">
                  <table className="table table-bordered table-sm table-striped align-middle">
                    <thead style={{ background: "#6c757d", color: "#fff" }}>
                      <tr>
                        <th style={{ width: 40 }}>#</th>
                        <th>Date</th>
                        <th>Bill No.</th>
                        <th>Type</th>
                        <th className="text-end">Bill Amount (₹)</th>
                        <th className="text-end">Paid (₹)</th>
                        <th className="text-end">Balance (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.bills.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center text-muted py-3">
                            No pending bills
                          </td>
                        </tr>
                      ) : (
                        data.bills.map((bill, idx) => (
                          <tr key={`${bill.bill_type}-${bill.id}`}>
                            <td>{idx + 1}</td>
                            <td>{fmt(bill.bill_date)}</td>
                            <td>{bill.bill_no}</td>
                            <td>
                              <span
                                className={`badge ${
                                  bill.bill_type === "patient_entry"
                                    ? "bg-info"
                                    : "bg-secondary"
                                }`}
                              >
                                {bill.bill_type === "patient_entry" ? "Test" : "Billing"}
                              </span>
                            </td>
                            <td className="text-end">{fmtAmt(bill.bill_amount)}</td>
                            <td className="text-end">{fmtAmt(bill.paid_amount)}</td>
                            <td className="text-end fw-semibold text-danger">
                              {fmtAmt(bill.balance_amount)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="card border-0 bg-light h-100">
                      <div className="card-body">
                        <h6 className="card-title text-muted mb-3">Summary</h6>
                        {[
                          { label: "Total Bill Amount", value: data.totals.total_bill_amount, cls: "text-dark" },
                          { label: "Total Paid",        value: data.totals.total_paid_amount, cls: "text-success" },
                          { label: "Balance Due",       value: data.totals.total_balance_amount, cls: "text-danger fw-bold" },
                          { label: "Payable Amount",    value: data.totals.payable_amount, cls: "text-primary fw-bold" },
                        ].map(({ label, value, cls }) => (
                          <div
                            key={label}
                            className="d-flex justify-content-between border-bottom py-1"
                          >
                            <span className="text-muted">{label}</span>
                            <span className={cls}>₹ {value}</span>
                          </div>
                        ))}
                        <div className="mt-3">
                          <label className="form-label fw-semibold">Created By</label>
                          <input
                            type="text"
                            className="form-control"
                            value={currentUser}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card border-0 bg-light h-100">
                      <div className="card-body">
                        <h6 className="card-title text-muted mb-3">Payment Entry</h6>

                        <div className="mb-3">
                          <label className="form-label fw-semibold">Pay Amount (₹)</label>
                          <input
                            type="number"
                            className="form-control"
                            min={0}
                            step="0.01"
                            value={payAmount}
                            onChange={(e) => handlePayAmountChange(e.target.value)}
                            placeholder="0.00"
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label fw-semibold">Payment Method</label>
                          <select
                            className="form-select"
                            value={payMethod}
                            onChange={(e) => setPayMethod(e.target.value)}
                          >
                            {[
                              "Cash",
                              "Credit Card",
                              "Debit Card",
                              "Online Payment",
                              "Cheque",
                              "Demand Draft",
                            ].map((m) => (
                              <option key={m}>{m}</option>
                            ))}
                          </select>
                        </div>

                        <div className="mb-3">
                          <label className="form-label fw-semibold">Reference No.</label>
                          <input
                            type="text"
                            className="form-control"
                            value={refNo}
                            onChange={(e) => setRefNo(e.target.value)}
                            maxLength={100}
                            placeholder="Optional"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-light" onClick={onClose}>
              <i className="fa fa-arrow-left me-1" /> Back to List
            </button>
            <button
              type="button"
              className="btn btn-light"
              onClick={handleSubmit}
              disabled={saving || loading || !isValid}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Saving…
                </>
              ) : (
                <>
                  <i className="fa fa-check me-1" /> Submit Payment
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PDFViewerModal
// ─────────────────────────────────────────────────────────────────────────────
const PDFViewerModal: React.FC<{
  pdfUrl: string | null;
  title?: string;
  onClose: () => void;
}> = ({ pdfUrl, title = "Receipt", onClose }) => {
  if (!pdfUrl) return null;
  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
    >
      <div
        className="modal-dialog modal-xl"
        style={{ maxWidth: "90vw", height: "90vh" }}
      >
        <div className="modal-content h-100">
          <div className="modal-header py-2">
            <h6 className="modal-title mb-0">
              <i className="fa fa-file-pdf me-2 text-danger" />
              {title}
            </h6>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>
          <div
            className="modal-body p-0"
            style={{ height: "calc(100% - 55px)" }}
          >
            <iframe
              src={pdfUrl}
              title={title}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Payments (main page)
// ─────────────────────────────────────────────────────────────────────────────
const Payments: React.FC = () => {
  const navigate = useNavigate();
  const { user }                  = useAuth();
  const [activeTab, setActiveTab] = useState<"unpaid" | "receipts">("unpaid");

  const [financePatientId, setFinancePatientId] = useState<number | null>(null);
  const unpaidTableRef                          = useRef<any>(null);
  const receiptsTableRef                        = useRef<any>(null);

  const [pdfUrl, setPdfUrl]     = useState<string | null>(null);
  const [pdfTitle, setPdfTitle] = useState("Receipt");

  const fetchUnpaidBills = useCallback(
    async (page: number, search: string, sortBy = "id", sortOrder = "desc") => {
      try {
        const res = await API.get(
          `/payments/getUnpaidBills?page=${page}&pageSize=10&search=${encodeURIComponent(search)}&sortBy=${sortBy}&sortOrder=${sortOrder}`
        );
        return { data: res.data.data ?? [], total: res.data.total ?? 0 };
      } catch {
        toastr.error("Failed to fetch unpaid bills");
        return { data: [], total: 0 };
      }
    },
    []
  );

  const fetchReceipts = useCallback(
    async (page: number, search: string, sortBy = "id", sortOrder = "desc") => {
      try {
        const res = await API.get(
          `/payments/getPaymentReceipts?page=${page}&pageSize=10&search=${encodeURIComponent(search)}&sortBy=${sortBy}&sortOrder=${sortOrder}`
        );
        return { data: res.data.data ?? [], total: res.data.total ?? 0 };
      } catch {
        toastr.error("Failed to fetch payment receipts");
        return { data: [], total: 0 };
      }
    },
    []
  );

  const handleViewBillPDF = async (bill: UnpaidBill) => {
    try {
      if (bill.bill_type === "patient_entry") {
        navigate(`/lab/BillPdf/${bill.id}`);
        return;
      }

      const res = await API.get(`/billing/invoicePDF/${bill.id}`, {
        responseType: "blob",
      });
      const blob = URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      setPdfTitle("Bill Receipt");
      setPdfUrl(blob);
    } catch {
      toastr.error("Failed to load bill PDF");
    }
  };

  // ✅ FIXED: now accepts source_table and passes it as ?source= query param
  const handleViewReceiptPDF = async (
    pID: number,
    sourceTable: string = "customer_payments"
  ) => {
    try {
      const source = sourceTable === "payments" ? "payments" : "customer_payments";
      const res = await API.get(
        `/payments/paymentReceiptPDF/${pID}?source=${source}`,
        { responseType: "blob" }
      );
      const blob = URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      setPdfTitle("Payment Receipt");
      setPdfUrl(blob);
    } catch {
      toastr.error("Failed to load receipt PDF");
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
  };

  const unpaidColumns = useMemo(
    () => [
      { header: "#",          accessorKey: "sno",      cell: ({ row }: any) => row.original.sno ?? "—" },
      { header: "Date",       accessorKey: "bill_date", cell: ({ row }: any) => fmt(row.original.bill_date) },
      { header: "Bill No.",   accessorKey: "bill_no",   cell: ({ row }: any) => row.original.bill_no || "—" },
      { header: "Patient ID", accessorKey: "P_code",    cell: ({ row }: any) => row.original.P_code || "—" },
      {
        header: "Patient Name",
        id:     "patient_name",
        cell:   ({ row }: any) =>
          `${row.original.prefix || ""} ${row.original.P_name || ""}`.trim() || "—",
      },
      {
        header: "Bill Amt (₹)",
        accessorKey: "net_amount",
        cell: ({ row }: any) => (
          <span className="text-end d-block">{fmtAmt(row.original.net_amount)}</span>
        ),
      },
      {
        header: "Paid (₹)",
        accessorKey: "paid_amount",
        cell: ({ row }: any) => (
          <span className="text-end d-block">
            {fmtAmt(row.original.paid_amount)}
          </span>
        ),
      },
      {
        header: "Balance (₹)",
        accessorKey: "balance_amount",
        cell: ({ row }: any) => (
          <span className="text-end d-block text-danger fw-semibold">
            {fmtAmt(row.original.balance_amount)}
          </span>
        ),
      },
      {
        header: "Actions",
        id:     "actions",
        cell:   ({ row }: any) => {
          const bill: UnpaidBill = row.original;
          return (
            <div className="d-flex justify-content-center gap-1">
              <button
                className="btn btn-light"
                title="View Bill"
                onClick={() => handleViewBillPDF(bill)}
              >
                <i className="fa fa-eye" />
              </button>
              <button
                className="btn btn-light"
                title="Add Payment"
                onClick={() => setFinancePatientId(bill.patient_id)}
              >
                <i className="fab fa-paypal" />
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  const receiptColumns = useMemo(
    () => [
      { header: "#",    accessorKey: "sno",     cell: ({ row }: any) => row.original.sno ?? "—" },
      { header: "Date", accessorKey: "created", cell: ({ row }: any) => fmt(row.original.created) },
      {
        header: "Patient",
        id:     "patient",
        cell:   ({ row }: any) =>
          `${row.original.patient_name || ""}`.trim() || "—",
      },
      {
        header: "Description",
        accessorKey: "description",
        cell: ({ row }: any) => row.original.description || "—",
      },
      {
        header: "Amount (₹)",
        accessorKey: "amount",
        cell: ({ row }: any) => (
          <span className="text-end d-block fw-semibold">
            ₹ {fmtAmt(row.original.amount)}
          </span>
        ),
      },
      {
        header: "Actions",
        id:     "actions",
        // ✅ FIXED: pass source_table to handleViewReceiptPDF
        cell:   ({ row }: any) => (
          <div className="d-flex justify-content-center">
            <button
              className="btn btn-light"
              title="View Receipt"
              onClick={() =>
                handleViewReceiptPDF(row.original.id, row.original.source_table)
              }
            >
              <i className="fa fa-eye me-1" /> View
            </button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div>
      <ContentHeader title="Payments" />

      <section className="content">
        <div className="container-fluid">
          <ul className="nav nav-tabs mb-0">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "unpaid" ? "active" : ""}`}
                onClick={() => setActiveTab("unpaid")}
              >
                <i className="fa fa-file-invoice-dollar me-1" />
                Patient Payments
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "receipts" ? "active" : ""}`}
                onClick={() => setActiveTab("receipts")}
              >
                <i className="fa fa-receipt me-1" />
                Payment Receipts
              </button>
            </li>
          </ul>

          <div
            className="card border-top-0 rounded-0 rounded-bottom"
            style={{ display: activeTab === "unpaid" ? "block" : "none" }}
          >
            <div className="card-body">
              <DataTable
                ref={unpaidTableRef}
                columns={unpaidColumns}
                queryFn={fetchUnpaidBills}
                queryKey={["unpaid-bills"]}
                pageSize={10}
              />
            </div>
          </div>

          <div
            className="card border-top-0 rounded-0 rounded-bottom"
            style={{ display: activeTab === "receipts" ? "block" : "none" }}
          >
            <div className="card-body">
              <DataTable
                ref={receiptsTableRef}
                columns={receiptColumns}
                queryFn={fetchReceipts}
                queryKey={["payment-receipts"]}
                pageSize={10}
              />
            </div>
          </div>
        </div>
      </section>

      <PatientFinanceModal
        patientId={financePatientId}
        onClose={() => setFinancePatientId(null)}
        onSuccess={() => {
          setFinancePatientId(null);
          unpaidTableRef.current?.refetch();
          receiptsTableRef.current?.refetch();
        }}
        currentUser={user?.full_name || ""}
      />

      <PDFViewerModal
        pdfUrl={pdfUrl}
        title={pdfTitle}
        onClose={handleClosePDF}
      />
    </div>
  );
};

export default Payments;